import {MouseEvent as ReactMouseEvent, useEffect, useMemo, useRef, useState} from 'react';

export interface FilterOption {
    /** Underlying value stored in selection. */
    value: string;
    /** Display label shown in the dropdown and as the chip summary. */
    label: string;
}

interface Props {
    label: string;
    options: FilterOption[];
    selected: Set<string>;
    onChange: (next: Set<string>) => void;
    placeholder?: string;
}

export function MultiSelectFilter({label, options, selected, onChange, placeholder = 'Search…'}: Props) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) {
            return;
        }
        const onDocClick = (e: MouseEvent) => {
            if (!ref.current?.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);

    // Match against both label and underlying value so users can search by name OR id.
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) {
            return options;
        }
        return options.filter(
            o => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
        );
    }, [options, query]);

    const toggle = (value: string) => {
        const next = new Set(selected);
        if (next.has(value)) {
            next.delete(value);
        } else {
            next.add(value);
        }
        onChange(next);
    };

    const summary = (() => {
        if (selected.size === 0) {
            return 'All';
        }
        if (selected.size === 1) {
            const only = Array.from(selected)[0];
            return options.find(o => o.value === only)?.label ?? only;
        }
        return `${selected.size} selected`;
    })();

    const clearAll = (e: ReactMouseEvent) => {
        e.stopPropagation();
        onChange(new Set());
    };

    return (
        <div ref={ref} style={{position: 'relative'}}>
            <button className={`select${open ? ' open' : ''}`} onClick={() => setOpen(v => !v)} type="button">
                <span className="label">{label}:</span>
                <span className="value">{summary}</span>
                {selected.size > 0 && (
                    <span
                        className="select-clear"
                        role="button"
                        aria-label="Clear filter"
                        onClick={clearAll}
                    >
                        ×
                    </span>
                )}
                <svg className="caret" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
            </button>
            {open && (
                <div className="popover" role="listbox">
                    <div className="ps-search">
                        <input
                            type="text"
                            placeholder={placeholder}
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            autoFocus
                        />
                        {selected.size > 0 && (
                            <button
                                type="button"
                                className="btn btn-text"
                                style={{marginLeft: 8, fontSize: 12, padding: '0 8px', height: 28}}
                                onClick={() => onChange(new Set())}
                            >
                                Clear ({selected.size})
                            </button>
                        )}
                    </div>
                    {filtered.length === 0 ? (
                        <div className="empty-opt">No matches</div>
                    ) : (
                        filtered.map(opt => (
                            <label key={opt.value} className="opt">
                                <input
                                    type="checkbox"
                                    checked={selected.has(opt.value)}
                                    onChange={() => toggle(opt.value)}
                                />
                                <span>{opt.label}</span>
                            </label>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
